import React, { PureComponent, type MouseEvent } from 'react';
import _ from 'lodash';

import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import { fedopsLogger, editorWixRecorder } from '#packages/util';
import * as coreBi from '#packages/coreBi';
import constants from '#packages/constants';
import type { ProgressStatus } from '#packages/constants';
import type { PublishInteractionStartedSource } from 'types/fedops/publishInteraction';

import withSendBi, { type WithSendBiProps } from '../../hocs/withSendBi';
import withOpenDropPanel from '../../hocs/withOpenDropPanel';
import withDropDown, {
  type ComponentWithDropDownOnHoverProps,
} from '../../hocs/withDropDownAction/withDropDownOnHover';
import type { WithDropDownOnActionDropDownProps } from '../../hocs/withDropDownAction/withDropDownTypes';
import withCustomDropPanel from '../../hocs/withCustomDropPanel';
import withDropPanelBiBehavior from './withDropPanelBiBehavior';
import withPublishDropPanel from './withDropPanel';

import { mapStateToProps, mapDispatchToProps } from './publishMappers';

import TopBarButton from '../../topBarButton/topBarButton';
import ActionSuccessSymbol from '../../actionSuccess/actionSuccessSymbol';

import type {
  PublishOptions,
  SaveAndPublishOptions,
} from '#packages/savePublish';

const { BUTTONS } = constants.ROOT_COMPS.TOPBAR;

const isInProgress = (status: ProgressStatus) =>
  status === constants.PROGRESS_STATUS.IN_PROGRESS;
const isDoneSuccessfully = (status: ProgressStatus) =>
  status === constants.PROGRESS_STATUS.DONE_SUCCESS;

export interface PublishButtonOwnProps {
  className?: string;
}

export interface PublishButtonStateProps {
  status: ProgressStatus;
  disabled: boolean;
  shouldSaveBeforePublish: boolean;
  isMobileEditor?: boolean;
  isInPreviewMode?: boolean;
  isSitePublished?: boolean;
  numberOfManualPublish: number;
  topBarStateBIParamValue: string;
  devMode: 'on' | 'off';
  clickId: string;
  isCustomTemplate: boolean;
  isCustomTemplatePublished?: boolean;
}

export interface PublishButtonDispatchProps {
  publish: (options: PublishOptions) => void;
  saveAndPublish: (options: SaveAndPublishOptions) => void;
}

type PublishButtonProps = PublishButtonOwnProps &
  PublishButtonStateProps &
  PublishButtonDispatchProps &
  WithSendBiProps &
  ComponentWithDropDownOnHoverProps;

class PublishButton extends PureComponent<
  PublishButtonProps & WithSendBiProps
> {
  handleClick = () => {
    const sourceOfStart: PublishInteractionStartedSource =
      'topBar_publishButton';

    // NOTE: if "shouldSaveBeforePublish", instead of publish, there will save and save success panel. Then user should click "publish" click on the panel.
    if (!this.props.shouldSaveBeforePublish) {
      // NOTE: https://github.com/wix-private/santa-editor/issues/37029
      fedopsLogger.interactionStarted(fedopsLogger.INTERACTIONS.PUBLISH, {
        paramsOverrides: {
          sourceOfStart,
        },
      });

      this.props.publish({
        sourceOfStart,
        dsPublishOptions: { clickId: this.props.clickId },
      });
    } else {
      this.props.saveAndPublish({
        sourceOfStart,
        origin: 'top_bar_publish',
        dsPublishOptions: { clickId: this.props.clickId },
      });
    }
    editorWixRecorder.addLabel('publish top bar clicked');
    this.props.sendBi(coreBi.events.topbar.top_bar_click, {
      category: 'publish',
      origin: this.props.isInPreviewMode ? 'preview' : 'editor',
      isPreview: this.props.isInPreviewMode,
      is_published: this.props.isSitePublished,
      state: this.props.topBarStateBIParamValue,
    });

    const currentViewMode = util.publishUtil.getPublishViewMode(
      this.props.isMobileEditor,
      this.props.isInPreviewMode,
    );
    const numberOfManualPublish = this.props.numberOfManualPublish
      ? this.props.numberOfManualPublish + 1
      : 1;

    this.props.sendBi(coreBi.events.publish.PUBLISH_CLICK, {
      origin: 'topbar',
      viewModeSource: currentViewMode,
      numberOfManualPublish,
      devMode: this.props.devMode,
      clickId: this.props.clickId,
    });

    if (this.props.isCustomTemplate) {
      this.props.sendBi(
        coreBi.events.customTemplate.click_on_publish_template,
        {
          origin: 'topbar',
          is_first_template_publish: !this.props.isCustomTemplatePublished,
        },
      );
    }
  };

  getLabel = (): string => {
    if (this.props.isCustomTemplate) {
      return translate('TOPBAR_Templates_Publish_Template_Button');
    }
    return translate('TOPBAR_Site_Publish');
  };

  onMouseEnter = (event: MouseEvent) => {
    this.props.onMouseEnter(event);

    if (this.props.isCustomTemplate) {
      this.props.sendBi(
        coreBi.events.customTemplate.hover_on_publish_template,
        {
          action: 'hover',
          is_published_template: this.props.isCustomTemplatePublished,
        },
      );
    }
  };

  render() {
    const doneSuccessfully = isDoneSuccessfully(this.props.status);

    const className = util.cx({
      'top-bar-publish-btn': true,
      'top-bar-btn-in-progress-indicator': isInProgress(this.props.status),
      'top-bar-btn-done-successfully': doneSuccessfully,
      [this.props.className]: true,
    });

    const customSymbol = doneSuccessfully
      ? React.createElement(ActionSuccessSymbol)
      : null;

    return (
      <TopBarButton
        automationId="top-bar-button-publish"
        disabled={this.props.disabled}
        label={this.getLabel()}
        onClick={this.handleClick}
        className={className}
        customSymbol={customSymbol}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
      />
    );
  }
}

const PublishButtonWithDropDown = _.flow(
  util.hoc.connect(
    util.hoc.STORES.EDITOR_API,
    mapStateToProps,
    mapDispatchToProps,
  ),
  withDropDown,
  withDropPanelBiBehavior,
  withCustomDropPanel(BUTTONS.PUBLISH),
  withPublishDropPanel,
  withOpenDropPanel,
  withSendBi,
)(PublishButton) as React.ComponentType<
  PublishButtonOwnProps & WithDropDownOnActionDropDownProps
>;

PublishButtonWithDropDown.defaultProps = {
  panelTopMargin: 11,
};

(PublishButtonWithDropDown as AnyFixMe).pure = PublishButton;

export default PublishButtonWithDropDown;
